import { ref } from 'vue';

/**
 * Composable for table sorting functionality
 * @param {Function} fetchCallback - Function to call when sort changes (optional)
 * @param {string} initialField - Initial field to sort by (default: 'lastname')
 * @param {string} initialOrder - Initial sort order 'asc' or 'desc' (default: 'asc')
 * @returns {Object} Sorting state and methods
 */
export function useTableSort(fetchCallback = null, initialField = 'lastname', initialOrder = 'asc') {
    const sortField = ref(initialField);
    const sortOrder = ref(initialOrder);

    // Preload sort icons
    const sortAscIcon = require('@/assets/icons/sort_asc.svg');
    const sortDescIcon = require('@/assets/icons/sort_dsc.svg');
    const sortUnsortedIcon = require('@/assets/icons/sort_unsorted.svg');

    /**
     * Get the appropriate sort icon for a field
     * @param {string} field - The field to get the icon for
     * @returns {string} Path to the sort icon
     */
    const getSortIcon = (field) => {
        if (sortField.value === field) {
            return sortOrder.value === 'asc' ? sortAscIcon : sortDescIcon;
        }
        return sortUnsortedIcon;
    };

    /**
     * Sort by a specific field
     * @param {string} field - The field to sort by
     */
    const sortBy = (field) => {
        if (sortField.value === field) {
            // Toggle order if same field
            sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
        } else {
            // New field, start with ascending
            sortField.value = field;
            sortOrder.value = 'asc';
        }

        // Call the callback if provided
        if (fetchCallback && typeof fetchCallback === 'function') {
            fetchCallback();
        }
    };

    /**
     * Reset sorting to initial values
     */
    const resetSort = () => {
        sortField.value = initialField;
        sortOrder.value = initialOrder;

        if (fetchCallback && typeof fetchCallback === 'function') {
            fetchCallback();
        }
    };

    return {
        sortField,
        sortOrder,
        getSortIcon,
        sortBy,
        resetSort
    };
}
